import { Component, OnDestroy, OnInit } from "@angular/core";
import { filter, Subject, Subscription, takeUntil } from "rxjs";
import { AppService } from "./app.service";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AuthService } from "./shared/auth/auth.service";
import { Router, RouterModule } from "@angular/router";
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import { commonEnv } from "src/environments/environment.common";
import { CommonModule } from "@angular/common";
import { MainNavComponent } from "./shared/components/main-nav/main-nav.component";
import { ContactSupportComponent } from "./shared/components/contact-support/contact-support.component";
import { routeList } from "./shared/models";
import { StoreService } from "./services-store/store.service";
import { removeUnUnloadedDocumentsFromStorage } from "./utils";

@Component({
    selector: "app-root",
    imports: [
        CommonModule,
        RouterModule,
        MainNavComponent,
        ContactSupportComponent,
    ],
    templateUrl: "./app.component.html",
    styleUrl: "./app.component.scss"
})
export class AppComponent implements OnInit, OnDestroy {
  title = "International Online Application";
  // holding displayed user name (declared while creating an account)
  displayedName: string;
  loadingText: string;
  loginFailure$: Subscription = null;
  acquireTokenFailure$: Subscription = null;
  isInvalidLoginAttempt = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    public appService: AppService,
    public msalService: MsalService,
    private authService: AuthService,
    private store: StoreService,
    private router: Router,
    private msalBroadcast: MsalBroadcastService
  ) {}

  ngOnInit(): void {
    removeUnUnloadedDocumentsFromStorage();
    this.loadingText = this.appService.setLoadingText();

    this.msalService.instance.setActiveAccount(
      this.msalService.instance.getAllAccounts()[0]
    );
    this.handleMsalEvents();

    const userInfo = this.authService.getUserInfo();

    if (userInfo) {
      // setting the username for application header
      this.displayedName = userInfo.name;

      if (userInfo.tfp === commonEnv.applicantSignInPolicy) {
        this.store.authStore.addNewUser();
        this.router.navigateByUrl(
          `/${routeList.application.path}/${routeList.application.children.personalInfo}`
        );
      } else if (userInfo.tfp === commonEnv.agentSignInPolicy) {
        // Original: this.store.dispatch(new VerifyAgent());
        /* 
          We needed to by pass verifyagent for the new Role system in place
          Originally we dispatched a VerifyAgent Action but now we skip that by
          dispatching a new user instead.
          */

        this.store.authStore.addNewUser(
          this.authService.getUserInfo().emails[0]
        );
        this.router.navigateByUrl(`/${routeList.agent.path}`);
      }
    }
  }

  handleMsalEvents() {
    // Handle MSAL events using the new event system
    this.msalBroadcast.msalSubject$
      .pipe(
        filter(
          (event: EventMessage) =>
            event.eventType === EventType.LOGIN_FAILURE ||
            event.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        )
      )
      .subscribe((event: EventMessage) => {
        const errorMessage = event.error?.message || "An error occurred";
        this.isInvalidLoginAttempt = true;
        this.handleMsalError(event.error);
      });

    this.msalBroadcast.inProgress$
    .pipe(
      filter(
          (status: InteractionStatus) => status === InteractionStatus.None
      ),
      takeUntil(this._destroying$)
  )
      .subscribe(() => {
        const activeAccount = this.msalService.instance.getActiveAccount();
        if (activeAccount) {
          this.displayedName = activeAccount.name || "";
          this.checkAndSetActiveAccount();
        }
      });
  }

  handleMsalError(error: any) {
    if (error.message.includes("AADB2C90091")) {
      // Handle forgotten password
      // this.authService.resetPassword();
    } else if (error.message.includes("AADB2C90118")) {
      // Handle password reset cancellation
      this.authService.login();
    } else if (error.message.includes("AADB2C90157")) {
      // Handle multiple accounts
      this.authService.login();
    } else {
      this.authService.logout();
    }
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     */
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  isLoginCompleted() {
    // Check if there are any active accounts
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts.length > 0;
  }

  ngOnDestroy(): void {
    if (this.loginFailure$) {
      this.loginFailure$.unsubscribe();
    }
    if (this.acquireTokenFailure$) {
      this.acquireTokenFailure$.unsubscribe();
    }

    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
